import type { ThunkAction, ThunkDispatch } from "@reduxjs/toolkit";
import type { IgfdPaymentPlan, Restaurant } from "api-types";

import { onRestaurantSelected } from "igfd-commons/src/monitoring";

import type {
  ReduxState,
  RestaurantDraft,
  RestaurantWithPaymentPlan,
  ThunkExtraArgument,
} from "@dashboard/types";

import { mixpanelWorkspaceCreated } from "../../mixpanel/mixpanel";
import { type ErrorReducerAction } from "../reducers/errorReducer";
import { type RestaurantReducerAction } from "../reducers/restaurantReducer";
import { addError } from "./addErrorAction";

export type RestaurantReducerDispatch = ThunkDispatch<
  ReduxState,
  ThunkExtraArgument,
  RestaurantReducerAction | ErrorReducerAction
>;

type RestaurantReducerThunkAction = ThunkAction<
  void,
  ReduxState,
  ThunkExtraArgument,
  RestaurantReducerAction
>;

type RestaurantReducerThunkPromiseAction = ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkExtraArgument,
  RestaurantReducerAction | ErrorReducerAction
>;

// Every selection dispatch goes through here so RESTAURANT_CHANGED fires
// only when the selection actually moved to a different restaurant.
const selectRestaurant: (
  payload: RestaurantWithPaymentPlan | null
) => RestaurantReducerThunkAction = (
  payload: RestaurantWithPaymentPlan | null
) => {
  return (dispatch: RestaurantReducerDispatch, getState: () => ReduxState) => {
    const previousUuid = getState().restaurant.selectedRestaurant?.uuid;
    dispatch({ type: "RESTAURANT_SELECTED", payload });
    if (payload?.restaurant.uuid !== previousUuid) {
      dispatch({ type: "RESTAURANT_CHANGED" });
    }
  };
};

export const fetchAccessibleRestaurants: (
  userId: string, // firebase auth user id
  onError: (error: Error) => void
) => RestaurantReducerThunkAction = (
  userId: string,
  onError: (error: Error) => void
) => {
  return (
    dispatch: RestaurantReducerDispatch,
    getState: () => ReduxState,
    { restaurantApi }: ThunkExtraArgument
  ) => {
    // This is present when the page is refreshed, or during load from persisted state.
    const previouslySelectedRestUuid =
      getState().restaurant.selectedRestaurant?.uuid;
    const isAdminUser = getState().dashboardApp.userIsAdmin;

    restaurantApi
      .fetchAccessibleRestaurants(userId)
      .then((restaurantAndPlans) => {
        dispatch({ type: "FETCHED_RESTAURANTS", payload: restaurantAndPlans });

        // We want to dispatch the restaurant selected event to ensure all other parts of the app
        // that depend on this action being dispatched, works correctly.
        // However the result of the fetchAccessibleRestaurants won't contain the previously selected
        // restaurant for admin users. Hence for them we fetch this data again separately.
        if (isAdminUser && previouslySelectedRestUuid) {
          // If a admin user already had selected a restaurant, keep it selected.
          // The previous restaurant may not exist in the list of accessible restaurants,
          // so we refetch it separately.
          restaurantApi
            .fetchRestaurantAndPaymentPlan(previouslySelectedRestUuid)
            .then((restaurantAndPlan) => {
              dispatch(selectRestaurant(restaurantAndPlan));
            })
            .catch(onError);
        } else {
          const previouslySelectedRestaurant = restaurantAndPlans.find(
            (r) => r.restaurant.uuid === previouslySelectedRestUuid
          );

          // If no restaurant was previously selected (first load without local state) or
          // the user no longer has access to the previously selected restaurant, select
          // the first restaurant by default if there is one.
          const restaurantToSelect =
            previouslySelectedRestaurant || restaurantAndPlans[0] || null;

          dispatch(selectRestaurant(restaurantToSelect));
        }
      })
      .catch(onError);
  };
};

export const restaurantSelectedAction: (
  restaurant: Restaurant
) => ThunkAction<
  void,
  ReduxState,
  ThunkExtraArgument,
  RestaurantReducerAction
> = (restaurant: Restaurant) => {
  return (dispatch: RestaurantReducerDispatch, getState: () => ReduxState) => {
    onRestaurantSelected(restaurant); // track in analytics (set restaurant uuid in datadog)

    // The restaurant is one the user has access to, so its plan is already in
    // the store from the accessible-restaurants fetch.
    const paymentPlan =
      getState().restaurant.paymentPlansByRestaurantUuid[restaurant.uuid];
    dispatch(selectRestaurant({ restaurant, paymentPlan }));
  };
};

export const restaurantCreatedAction: (
  restaurantWithPlan: RestaurantWithPaymentPlan
) => RestaurantReducerThunkAction = (
  restaurantWithPlan: RestaurantWithPaymentPlan
) => {
  return (dispatch: RestaurantReducerDispatch) => {
    dispatch({ type: "RESTAURANT_CREATED", payload: restaurantWithPlan });
    mixpanelWorkspaceCreated(
      restaurantWithPlan.restaurant,
      restaurantWithPlan.paymentPlan
    );
    // select the newly created restaurant (triggers the per-restaurant reducers)
    dispatch(restaurantSelectedAction(restaurantWithPlan.restaurant));
  };
};

/**
 * A restaurant selected by an admin user, from the Admin Mode page.
 *
 * This involves setting the payment plan as well.
 */
export const restaurantSelectedByAdminAction: (
  restaurant: Restaurant,
  paymentPlan: IgfdPaymentPlan
) => RestaurantReducerThunkAction = (
  restaurant: Restaurant,
  paymentPlan: IgfdPaymentPlan
) => {
  return (dispatch: RestaurantReducerDispatch) => {
    onRestaurantSelected(restaurant); // track in analytics
    dispatch(selectRestaurant({ restaurant, paymentPlan }));
  };
};

export const restaurantPlanUpdatedAction = (
  restaurantUuid: string,
  paymentPlan: IgfdPaymentPlan,
  restaurant: Restaurant
): RestaurantReducerAction => ({
  type: "RESTAURANT_PLAN_UPDATED",
  payload: { restaurantUuid, restaurant, paymentPlan },
});

// Updates the restaurant entity in firestore
export const updateRestaurant: (
  restaurant: Partial<RestaurantDraft>
) => RestaurantReducerThunkPromiseAction = (
  restaurant: Partial<RestaurantDraft>
) => {
  return (
    dispatch: RestaurantReducerDispatch,
    getState: () => ReduxState,
    { restaurantApi }: ThunkExtraArgument
  ) => {
    const { selectedRestaurant } = getState().restaurant;
    if (!selectedRestaurant) {
      return Promise.resolve();
    }

    return restaurantApi
      .updateRestaurantDetails(selectedRestaurant.uuid, restaurant)
      .then(() => {
        dispatch({
          type: "RESTAURANT_DETAILS_UPDATED",
          payload: restaurant,
        });
      })
      .catch(addError(dispatch));
  };
};
